import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Eye,
  Loader2,
  Smartphone,
  UserCheck,
  UserX,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import CompanyAdminLayout from "../../components/CompanyAdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { companyAdminAppInstallationsAPI } from "../../lib/companyAdminApi";
import CustomerMembershipBadge from "../../components/CustomerMembershipBadge";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const truncateToken = (token) => {
  if (!token) return "—";
  if (token.length <= 20) return token;
  return `${token.slice(0, 10)}…${token.slice(-8)}`;
};

const AppInstallationsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [installations, setInstallations] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [registeredOnly, setRegisteredOnly] = useState(false);
  const [anonymousOnly, setAnonymousOnly] = useState(false);
  const [stats, setStats] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadInstallations = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        companyAdminAppInstallationsAPI.getAll({
          page,
          limit: 20,
          search: search.trim() || undefined,
          registered_only: registeredOnly ? "1" : undefined,
          unregistered_only: anonymousOnly ? "1" : undefined,
        }),
        companyAdminAppInstallationsAPI.getOverviewStats(),
      ]);
      setInstallations(listRes.data.installations || []);
      setTotal(listRes.data.total || 0);
      setPages(listRes.data.pages || 1);
      setStats(statsRes.data || null);
    } catch {
      toast.error("Failed to load app installations");
    } finally {
      setLoading(false);
    }
  }, [page, search, registeredOnly, anonymousOnly]);

  useEffect(() => {
    loadInstallations();
  }, [loadInstallations]);

  const openDetail = async (row) => {
    if (row.user?.user_id || row.user?._id) {
      navigate(`/admin/customers/${row.user.user_id || row.user._id}`);
      return;
    }

    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await companyAdminAppInstallationsAPI.getOne(row._id);
      setDetail(res.data);
    } catch {
      toast.error("Failed to load installation details");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRegisteredFilter = (checked) => {
    setRegisteredOnly(checked);
    if (checked) setAnonymousOnly(false);
    setPage(1);
  };

  const handleAnonymousFilter = (checked) => {
    setAnonymousOnly(checked);
    if (checked) setRegisteredOnly(false);
    setPage(1);
  };

  return (
    <CompanyAdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0e1726] flex items-center gap-2">
              <Smartphone className="w-7 h-7 text-primary" />
              App Installations
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Devices that installed the app — linked to customers after
              registration.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="panel border border-border bg-card p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total devices</p>
              <p className="text-2xl font-bold text-[#0e1726]">
                {stats?.total ?? total}
              </p>
            </div>
          </div>
          <div className="panel border border-border bg-card p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
              <UserX className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">No signup (retarget)</p>
              <p className="text-2xl font-bold text-[#0e1726]">
                {stats?.anonymous ?? "—"}
              </p>
            </div>
          </div>
          <div className="panel border border-border bg-card p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-green-100 text-green-600 flex items-center justify-center shrink-0">
              <UserCheck className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Registered</p>
              <p className="text-2xl font-bold text-[#0e1726]">
                {stats?.registered ?? "—"}
              </p>
            </div>
          </div>
          <div className="panel border border-border bg-card p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">With FCM token</p>
              <p className="text-2xl font-bold text-[#0e1726]">
                {stats?.with_fcm ?? "—"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search device id, email, timezone..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-10"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={registeredOnly}
              onChange={(e) => handleRegisteredFilter(e.target.checked)}
              className="rounded border-input"
            />
            Registered only
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={anonymousOnly}
              onChange={(e) => handleAnonymousFilter(e.target.checked)}
              className="rounded border-input"
            />
            No signup only (for push retargeting)
          </label>
        </div>

        <div className="panel border border-border bg-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : installations.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              No app installations found.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px] max-w-[100px]">
                    Device ID
                  </TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Timezone</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>FCM Token</TableHead>
                  <TableHead>Installed</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {installations.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell
                      className="font-mono text-xs w-[100px] max-w-[100px] truncate"
                      title={row.device_id}
                    >
                      {row.device_id || "—"}
                    </TableCell>
                    <TableCell>{row.email || row.user?.email || "—"}</TableCell>
                    <TableCell>
                      {row.user ? (
                        <div className="space-y-1">
                          <div className="font-medium text-[#0e1726]">
                            {row.user.name || "—"}
                          </div>
                          <CustomerMembershipBadge customer={row.user} />
                        </div>
                      ) : (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                          Not registered
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{row.timezone || "—"}</TableCell>
                    <TableCell className="capitalize">
                      {row.device_type || "—"}
                    </TableCell>
                    <TableCell
                      className="font-mono text-xs max-w-[120px] truncate"
                      title={row.fcm_token}
                    >
                      {truncateToken(row.fcm_token)}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatDateTime(row.installed_at || row.createdAt)}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatDateTime(row.last_seen_at || row.updatedAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDetail(row)}
                        title={row.user ? "View customer" : "View installation"}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page} of {pages} ({total} total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Installation Details</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : detail?.installation ? (
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Device ID</dt>
                <dd className="font-mono break-all">
                  {detail.installation.device_id}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Timezone</dt>
                <dd>{detail.installation.timezone || "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Device Type</dt>
                <dd className="capitalize">
                  {detail.installation.device_type || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">FCM Token</dt>
                <dd className="font-mono text-xs break-all">
                  {detail.installation.fcm_token || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Installed At</dt>
                <dd>
                  {formatDateTime(
                    detail.installation.installed_at ||
                      detail.installation.createdAt,
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Last Seen</dt>
                <dd>
                  {formatDateTime(
                    detail.installation.last_seen_at ||
                      detail.installation.updatedAt,
                  )}
                </dd>
              </div>
              {detail.customer && (
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground mb-2">Linked customer</p>
                  <Link
                    to={`/admin/customers/${detail.customer.user_id || detail.customer._id}`}
                    className="text-primary hover:underline font-medium"
                  >
                    {detail.customer.name || detail.customer.email}
                  </Link>
                </div>
              )}
            </dl>
          ) : null}
        </DialogContent>
      </Dialog>
    </CompanyAdminLayout>
  );
};

export default AppInstallationsPage;
